/**
 * One planned trade → one Pokémon GO search string.
 *
 * The Search button used to copy the SPECIES NAME. That finds the species, but
 * not the copy you actually planned to trade: search "Vulpix" with a Shiny
 * Vulpix trade earmarked and you get every Vulpix you own, including the ones
 * you can't send. So the string now says all three things the trade row knows —
 * WHICH species, WHICH variant, and that it must still be tradable:
 *
 *   `25&shiny&!traded`
 *
 * By DEX NUMBER, never the name: it is exact, form-proof and spelling-proof,
 * and it survives a species whose in-game name differs from ours.
 *
 * `!traded` is on every string and is not optional — a Pokémon can only be
 * traded once, so a copy you received in a trade cannot be sent on. Without it
 * the search happily offers copies that will be refused at the counter.
 *
 * Tokens come from the same vocabulary as the trade-string builder
 * (lib/tradeString.ts FILTER_META), so the two features can never disagree about
 * what Pokémon GO calls a filter.
 *
 * ONLY WHAT SURVIVES THE TRADE IS SEARCHED FOR. The string finds the copy you
 * are about to SEND, and a trade does not preserve everything: Lucky is
 * conferred BY the trade, and the IVs are re-rolled on arrival, so neither is a
 * property the copy in your bag has yet. Searching `lucky` or `4*` for a Lucky
 * or 100% trade therefore looked for a Pokémon that cannot exist — your
 * existing luckies cannot be traded on at all, and your existing hundos are not
 * what the trade is for. Those trades search by number and `!traded` alone.
 *
 * Shiny, size and form DO survive, so they stay in the string: a shiny sent is
 * a shiny received. Reported on device 2026-08-13.
 */

/**
 * Dex variant → the in-game search token, or null for variants GO has no filter
 * for. A null drops the term rather than guessing: an unrecognised token makes
 * Pokémon GO return NOTHING, which is far worse than a slightly broad search.
 */
// Exported (2026-08-28) so tradeSearchCopy + the Dex page copy button speak
// the same vocabulary; this stays the ONE table of what GO calls a filter.
export const VARIANT_SEARCH_TOKEN: Record<string, string | null> = {
  // MAIN has no token. The species number alone is the filter.
  pokemon: null,
  shiny: "shiny",
  shadow: "shadow",
  purified: "purified",
  xxl: "xxl",
  xxs: "xxs",
  gmax: "gigantamax",
  mega: "megaevolve",
  // Lucky is conferred BY the trade, so no copy in your bag is one yet, and the
  // ones that are cannot be traded on. Number and !traded only.
  lucky: null,
  // The IVs re-roll on arrival, so a 100% is what you HOPE to receive, never
  // what you send. GO spells it "4*"; searching it here finds the wrong copy.
  perfect: null,
  // The shiny half of each of these survives the trade; the lucky / 100% half
  // does not, so only the shiny term is searched for.
  shundo: "shiny",
  shinyLucky: "shiny",
  // GO's search knows "dynamax" (2026-09-19, trade hub Dynamax pill).
  dynamax: "dynamax",
};

export interface TradeSearchInput {
  speciesId: number;
  variant: string;
}

/**
 * Build the search string for one planned trade. Returns "" when the species id
 * is unusable, so a caller can decline to copy rather than paste nonsense.
 */
export function buildTradeSearchString({ speciesId, variant }: TradeSearchInput): string {
  if (!Number.isFinite(speciesId) || speciesId < 1) return "";
  const token = VARIANT_SEARCH_TOKEN[variant] ?? null;
  return [String(Math.trunc(speciesId)), token, "!traded"].filter(Boolean).join("&");
}
